'use client';

import React, { useCallback, useEffect, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { EmblaOptionsType, EmblaPluginType } from 'embla-carousel';

type CarouselProps = {
    children: React.ReactNode;
    className?: string;
    options?: EmblaOptionsType;
    plugins?: EmblaPluginType[];
    showArrows?: boolean;
};

export const Carousel = ({
    children,
    className,
    options = { loop: true, align: 'start' },
    plugins,
    showArrows = true,
}: CarouselProps) => {
    // Default to Autoplay if no plugins provided, for backward compatibility or simple use cases
    // However, if the user explicitly passes plugins (even empty array), we use that.
    const activePlugins = plugins || [Autoplay({ delay: 5000, stopOnInteraction: false })];

    const [emblaRef, emblaApi] = useEmblaCarousel(options, activePlugins);

    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);
    const [isHovered, setIsHovered] = useState(false);

    const scrollPrev = useCallback(() => {
        if (emblaApi) emblaApi.scrollPrev();
    }, [emblaApi]);

    const scrollNext = useCallback(() => {
        if (emblaApi) emblaApi.scrollNext();
    }, [emblaApi]);

    const onSelect = useCallback((api: any) => {
        setCanScrollPrev(api.canScrollPrev());
        setCanScrollNext(api.canScrollNext());
    }, []);

    useEffect(() => {
        if (!emblaApi) return;

        onSelect(emblaApi);
        emblaApi.on('reInit', onSelect);
        emblaApi.on('select', onSelect);

        return () => {
            emblaApi.off('reInit', onSelect);
            emblaApi.off('select', onSelect);
        };
    }, [emblaApi, onSelect]);

    return (
        <div
            className={cn('relative', className)}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <div className="overflow-hidden" ref={emblaRef}>
                <div className="flex -ml-6 touch-pan-y">
                    {React.Children.map(children, (child) => (
                        <div className="pl-6 min-w-0 flex-[0_0_100%] md:flex-[0_0_50%] lg:flex-[0_0_33.333%]">
                            {child}
                        </div>
                    ))}
                </div>
            </div>

            {showArrows && (
                <>
                    <button
                        onClick={scrollPrev}
                        className={cn(
                            "absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 w-10 h-10 rounded-full bg-white shadow-md flex items-center justify-center text-slate-600 transition-opacity disabled:opacity-0 z-10 hover:bg-slate-50",
                            isHovered ? "opacity-100" : "opacity-0",
                            !canScrollPrev && "hidden"
                        )}
                        disabled={!canScrollPrev}
                    >
                        <ChevronLeft className="w-6 h-6" />
                    </button>

                    <button
                        onClick={scrollNext}
                        className={cn(
                            "absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 w-10 h-10 rounded-full bg-white shadow-md flex items-center justify-center text-slate-600 transition-opacity disabled:opacity-0 z-10 hover:bg-slate-50",
                            isHovered ? "opacity-100" : "opacity-0",
                            !canScrollNext && "hidden"
                        )}
                        disabled={!canScrollNext}
                    >
                        <ChevronRight className="w-6 h-6" />
                    </button>
                </>
            )}
        </div>
    );
};

